import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  LinearProgress,
  Alert,
  Pagination,
  CircularProgress,
  InputAdornment,
  Container,
  TablePagination,
} from "@mui/material";
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  Storage,
  LocationOn,
  Search,
} from "@mui/icons-material";
import { racksAPI } from "../../services/API";
import { useSnackbar } from "notistack";
import { useAuth } from "../../contexts/AuthContext";

export const Racks = () => {
  const navigate = useNavigate();
  const [racks, setRacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [zoneFilter, setZoneFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // Fixed: default to "all" to show all racks including inactive
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRack, setSelectedRack] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  const { enqueueSnackbar } = useSnackbar();
  const { hasAnyRole } = useAuth();

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRacks();
    }, 400);
    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, searchTerm, zoneFilter, statusFilter]);

  const fetchRacks = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        ...(zoneFilter !== "all" && { zone: zoneFilter }),
        ...(statusFilter !== "all" && { isActive: statusFilter }),
      };

      const response = await racksAPI.getAll(params);
      const { racks: racksData, pagination: paginationData } =
        response.data.data;

      setRacks(racksData || []);
      setPagination((prev) => ({
        ...prev,
        total: paginationData.total,
        pages: paginationData.pages,
      }));
    } catch (error) {
      enqueueSnackbar("Error fetching racks", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleZoneFilter = (value) => {
    setZoneFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, page: value }));
  };

  const handleMenuOpen = (event, rack) => {
    setAnchorEl(event.currentTarget);
    setSelectedRack(rack);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handleView = () => {
    if (selectedRack) navigate(`/hospital/pharmacy/racks/${selectedRack._id}`);
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedRack) navigate(`/hospital/pharmacy/racks/${selectedRack._id}/edit`);
    handleMenuClose();
  };

  const handleDelete = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const deleteRack = async () => {
    if (!selectedRack) return;
    try {
      await racksAPI.delete(selectedRack._id);
      enqueueSnackbar("Rack deleted successfully", { variant: "success" });
      setDeleteDialogOpen(false);
      setSelectedRack(null);
      fetchRacks();
    } catch (error) {
      const msg = error.response?.data?.message || "Error deleting rack";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const getZoneColor = (zone) => {
    const colors = {
      general: "primary",
      cold_storage: "info",
      controlled: "warning",
      hazardous: "error",
      quarantine: "secondary",
    };
    return colors[zone] || "default";
  };

  const getUtilizationColor = (utilization) => {
    if (utilization >= 90) return "error";
    if (utilization >= 75) return "warning";
    return "success";
  };

  const formatZoneName = (zone) =>
    zone
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)",
        fontSize: '16px',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h3" fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Rack Management
          </Typography>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate("/hospital/pharmacy/racks/new")}
              sx={{
                borderRadius: 2,
                boxShadow: 2,
                fontSize: '1.1rem',
                px: 3,
                py: 1,
                transition: "0.3s",
                "&:hover": { boxShadow: 4, transform: "scale(1.05)" },
              }}
            >
              Add Rack
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Card
          sx={{
            mb: 4,
            borderRadius: 3,
            boxShadow: 3,
            transition: "0.3s",
            "&:hover": { boxShadow: 5 },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Search racks by name or description..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: '1.5rem' }} color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '1.1rem',
                      py: 2,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Zone</InputLabel>
                  <Select
                    value={zoneFilter}
                    label="Zone"
                    onChange={(e) => handleZoneFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All Zones</MenuItem>
                    <MenuItem value="general" sx={{ fontSize: '1.1rem' }}>General</MenuItem>
                    <MenuItem value="cold_storage" sx={{ fontSize: '1.1rem' }}>Cold Storage</MenuItem>
                    <MenuItem value="controlled" sx={{ fontSize: '1.1rem' }}>Controlled</MenuItem>
                    <MenuItem value="hazardous" sx={{ fontSize: '1.1rem' }}>Hazardous</MenuItem>
                    <MenuItem value="quarantine" sx={{ fontSize: '1.1rem' }}>Quarantine</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => handleStatusFilter(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                  >
                    <MenuItem value="all" sx={{ fontSize: '1.1rem' }}>All</MenuItem>
                    <MenuItem value="true" sx={{ fontSize: '1.1rem' }}>Active</MenuItem>
                    <MenuItem value="false" sx={{ fontSize: '1.1rem' }}>Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="h6" color="text.secondary" align="center" sx={{ fontSize: '1.1rem' }}>
                  Total: {pagination.total} racks
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Racks Table */}
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: 3,
            transition: "0.3s",
            "&:hover": { boxShadow: 5 },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
              Racks
            </Typography>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Rack Name</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Location</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Zone</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Capacity</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Utilization</TableCell>
                        <TableCell sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Status</TableCell>
                        <TableCell align="center" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' }, fontWeight: 600, py: 2 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {racks.map((rack) => {
                        const utilization =
                          rack.capacity > 0
                            ? (rack.currentOccupancy / rack.capacity) * 100
                            : 0;

                        return (
                          <TableRow
                            key={rack._id}
                            hover
                            sx={{
                              "&:hover": { backgroundColor: "#fafafa" },
                              opacity: rack.isActive ? 1 : 0.7,
                            }}
                          >
                            <TableCell sx={{ py: 2.5 }}>
                              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                {rack.name}
                              </Typography>
                              {rack.description && (
                                <Typography
                                  variant="body1"
                                  color="text.secondary"
                                  sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                                >
                                  {rack.description}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 2.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <LocationOn fontSize="small" color="action" />
                                <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                  {[
                                    rack.location.aisle,
                                    rack.location.row,
                                    rack.location.section,
                                  ]
                                    .filter(Boolean)
                                    .join(" - ") || "Not specified"}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2.5 }}>
                              <Chip
                                label={formatZoneName(rack.location.zone)}
                                color={getZoneColor(rack.location.zone)}
                                size="medium"
                                sx={{ 
                                  borderRadius: 2, 
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8rem', md: '1rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ py: 2.5 }}>
                              <Typography variant="body1" sx={{ fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                {rack.currentOccupancy} / {rack.capacity}
                              </Typography>
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}
                              >
                                Available: {rack.capacity - rack.currentOccupancy}
                              </Typography>
                            </TableCell> 
                            <TableCell sx={{ py: 2.5 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(utilization, 100)}
                                  color={getUtilizationColor(utilization)}
                                  sx={{
                                    flexGrow: 1,
                                    height: 10,
                                    borderRadius: 4,
                                  }}
                                />
                                <Typography variant="body1" sx={{ minWidth: 40, fontSize: { xs: '0.9rem', md: '1.1rem' } }}>
                                  {utilization.toFixed(1)}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ py: 2.5 }}>
                              <Chip
                                label={rack.isActive ? "Active" : "Inactive"}
                                color={rack.isActive ? "success" : "default"}
                                size="medium"
                                sx={{ 
                                  borderRadius: 2, 
                                  fontWeight: 600,
                                  fontSize: { xs: '0.8rem', md: '1rem' }
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ py: 2.5 }}>
                              <IconButton
                                size="large"
                                onClick={(e) => handleMenuOpen(e, rack)}
                              >
                                <MoreVert sx={{ fontSize: '1.5rem' }} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* No Results */}
                {racks.length === 0 && (
                  <Box sx={{ textAlign: "center", py: 6 }}>
                    <Storage sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                    <Typography variant="h4" color="text.secondary" gutterBottom sx={{ fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                      No racks found
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, fontSize: '1.1rem' }}>
                      {searchTerm ||
                      zoneFilter !== "all" ||
                      statusFilter !== "all"
                        ? "Try adjusting your search filters"
                        : "Get started by creating your first rack"}
                    </Typography>
                    {hasAnyRole(["admin", "inventory_manager"]) && (
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate("/hospital/pharmacy/racks/new")}
                        sx={{
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          px: 3,
                          py: 1,
                          transition: "0.3s",
                          "&:hover": { transform: "scale(1.05)" },
                        }}
                      >
                        Create First Rack
                      </Button>
                    )}
                  </Box>
                )}

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                    <Pagination
                      count={pagination.pages}
                      page={pagination.page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Menu */}
        <Menu 
          anchorEl={anchorEl} 
          open={Boolean(anchorEl)} 
          onClose={handleMenuClose}
          sx={{
            '& .MuiMenuItem-root': {
              fontSize: '1.1rem',
            }
          }}
        >
          <MenuItem onClick={handleView}>
            <Visibility sx={{ mr: 1, fontSize: 24 }} /> View Details
          </MenuItem>
          {hasAnyRole(["admin", "inventory_manager"]) && (
            <MenuItem onClick={handleEdit}>
              <Edit sx={{ mr: 1, fontSize: 24 }} /> Edit Rack
            </MenuItem>
          )}
          {hasAnyRole(["admin"]) && (
            <MenuItem
              onClick={handleDelete}
              disabled={selectedRack?.currentOccupancy > 0}
              sx={{ color: "error.main" }}
            >
              <Delete sx={{ mr: 1, fontSize: 24 }} /> Delete Rack
            </MenuItem>
          )}
        </Menu>

        {/* Delete Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: '1.3rem' }}>Delete Rack</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2, fontSize: '1.1rem' }}>
              This action cannot be undone.
            </Alert>
            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
              Are you sure you want to delete rack{" "}
              <strong>{selectedRack?.name}</strong>?
            </Typography>
            {selectedRack?.currentOccupancy > 0 && (
              <Alert severity="error" sx={{ mt: 2, fontSize: '1.1rem' }}>
                Cannot delete rack that contains stock. Transfer or remove stock
                first.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontSize: '1.1rem' }}>
              Cancel
            </Button>
            <Button
              onClick={deleteRack}
              variant="contained"
              color="error"
              disabled={selectedRack?.currentOccupancy > 0}
              sx={{ fontSize: '1.1rem' }}
            >
              Delete Rack
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};